import React, { useEffect, useState } from 'react';
import { t } from '@/locales/strings';
import { track } from '@/utils/analytics';
import { isHintSeen, markHintSeen } from '@/utils/introState';
import useReducedMotion from '@/hooks/useReducedMotion';

/**
 * RouteHint — a one-time, dismissable banner that explains a screen
 * the first time a user lands on it. Gated by a localStorage key
 * unique to the hint so each user sees a given hint at most once.
 *
 * Used at the top of bottom-nav destinations (Calendar, Learning,
 * Profile) so orientation is contextual to the surface, not crammed
 * into a forced first-run modal. The matching strings live under
 * `route_hint_<key>_title` / `route_hint_<key>_body` and the dismiss
 * label is shared (`route_hint_dismiss`).
 *
 * Props:
 *   - hintKey:    name in `intro_state.hints` (e.g. `calendar`,
 *                 `learning`, `profile`, `session`, `method`)
 *   - titleKey:   strings.js key for the title
 *   - bodyKey:    strings.js key for the body
 *   - lang:       active locale ('en' | 'pt')
 */
export default function RouteHint({ hintKey, titleKey, bodyKey, lang = 'en' }) {
  const [open, setOpen] = useState(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!isHintSeen(hintKey)) {
      const id = setTimeout(() => {
        setOpen(true);
        track('route_hint_shown', { key: hintKey });
      }, 200);
      return () => clearTimeout(id);
    }
  }, [hintKey]);

  const dismiss = () => {
    markHintSeen(hintKey);
    track('route_hint_dismissed', { key: hintKey });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        margin: '12px 16px 4px',
        borderRadius: 18,
        padding: '14px 14px 12px',
        background:
          'linear-gradient(180deg, #fff 0%, var(--primary-pale, #fbeae7) 130%)',
        border: '1px solid color-mix(in srgb, var(--primary, #c97a68) 18%, transparent)',
        boxShadow: '0 10px 30px -18px rgba(45,31,38,.35)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        animation: reducedMotion ? 'none' : 'bb-hint-pop 280ms cubic-bezier(0.32,0.72,0,1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span aria-hidden="true" style={{
          flexShrink: 0,
          marginTop: 2,
          width: 22, height: 22,
          borderRadius: 999,
          background: 'var(--primary, #c97a68)',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          font: '700 12px/1 var(--font-ui, Manrope)',
        }}>
          ?
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            font: '500 15px/1.25 var(--font-title, Fraunces), serif',
            color: 'var(--ink, #2d1f26)',
            marginBottom: 2,
            textWrap: 'balance',
          }}>
            {t(titleKey, lang)}
          </div>
          <div style={{
            font: '400 13px/1.45 var(--font-body, Manrope)',
            color: 'var(--ink-dim, #6b5560)',
            textWrap: 'balance',
          }}>
            {t(bodyKey, lang)}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('route_hint_dismiss', lang)}
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            padding: 0,
            borderRadius: 999,
            border: 'none',
            background: 'transparent',
            color: 'var(--ink-dim, #6b5560)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        style={{
          alignSelf: 'flex-end',
          minHeight: 36,
          padding: '0 16px',
          borderRadius: 999,
          border: 'none',
          background:
            'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)',
          color: '#fff',
          font: '600 12px/1 var(--font-ui, Manrope)',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          boxShadow: '0 8px 18px -10px color-mix(in srgb, var(--primary, #c97a68) 55%, transparent)',
        }}
      >
        {t('route_hint_dismiss', lang)}
      </button>
      <style>{`
        @keyframes bb-hint-pop {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
